import React, { useState, useEffect, useCallback } from "react";
import styles from "./tetris.module.css";
import "bootstrap/dist/css/bootstrap.min.css";
import useUserInfo from "../../../Hooks/useUserInfo";
import { userGameRewards } from "../../../apis/user";
import { logPageView } from "../../../components/analytics";
import task from "../Task.js";
import { useAudio } from "../../../Providers/AudioProvider";

// Tetromino shapes
const TETROMINOES = {
  I: {
    shape: [[1, 1, 1, 1]],
    color: "#00f0f0",
  },
  J: {
    shape: [
      [1, 0, 0],
      [1, 1, 1],
    ],
    color: "#0000f0",
  },
  L: {
    shape: [
      [0, 0, 1],
      [1, 1, 1],
    ],
    color: "#f0a000",
  },
  O: {
    shape: [
      [1, 1],
      [1, 1],
    ],
    color: "#f0f000",
  },
  S: {
    shape: [
      [0, 1, 1],
      [1, 1, 0],
    ],
    color: "#00f000",
  },
  T: {
    shape: [
      [0, 1, 0],
      [1, 1, 1],
    ],
    color: "#a000f0",
  },
  Z: {
    shape: [
      [1, 1, 0],
      [0, 1, 1],
    ],
    color: "#f00000",
  },
};

const BOARD_WIDTH = 10;
const BOARD_HEIGHT = 18;
const INITIAL_SPEED = 1000; // milliseconds
const SPEED_INCREMENT = 50; // milliseconds
const POINTS_PER_LINE = 100;
const POINTS_PER_LEVEL = 1000;

const Tetris = () => {
  const [board, setBoard] = useState(createEmptyBoard());
  const [currentPiece, setCurrentPiece] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(() => {
    const saved = localStorage.getItem("tetrisScore");
    return saved ? parseInt(saved) : 0;
  });
  const [level, setLevel] = useState(() => {
    const saved = localStorage.getItem("tetrisLevel");
    return saved ? parseInt(saved) : 1;
  });
  const [lines, setLines] = useState(() => {
    const saved = localStorage.getItem("tetrisLines");
    return saved ? parseInt(saved) : 0;
  });
  const [isPaused, setIsPaused] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [highScore, setHighScore] = useState(() => {
    const saved = localStorage.getItem("tetrisHighScore");
    return saved ? parseInt(saved) : 0;
  });
  const [isNewHighScore, setIsNewHighScore] = useState(false);
  const playClick = useAudio();
  const [ghostPiece, setGhostPiece] = useState(null);

  const { userDetails, updateUserInfo, updatewatchScreenInfo, watchScreen } =
    useUserInfo();

  const IsPointsReached =
    watchScreen?.LoginMessage ===
    "You have already reached the maximum rewards limit";

  const toogleMenu = () => {
    playClick();
    updateUserInfo((prev) => ({
      ...prev,
      isPlay: false,
      currentComponent: task,
      currentComponentText: "task",
      lastComponent: userDetails?.userDetails.currentComponent,
      lastComponentText: userDetails?.userDetails.currentComponentText,
      isMenu: true,
      menuCount: userDetails?.userDetails?.menuCount + 1,
    }));
  };

  // Create empty board
  function createEmptyBoard() {
    return Array(BOARD_HEIGHT)
      .fill()
      .map(() => Array(BOARD_WIDTH).fill(0));
  }

  // Get random tetromino
  const getRandomTetromino = useCallback(() => {
    const shapes = Object.keys(TETROMINOES);
    const randomShape = shapes[Math.floor(Math.random() * shapes.length)];
    return {
      ...TETROMINOES[randomShape],
      position: { x: Math.floor(BOARD_WIDTH / 2) - 1, y: 0 },
    };
  }, []);

  // Check collision
  const checkCollision = useCallback(
    (piece, position) => {
      for (let y = 0; y < piece.shape.length; y++) {
        for (let x = 0; x < piece.shape[y].length; x++) {
          if (piece.shape[y][x]) {
            const newX = position.x + x;
            const newY = position.y + y;
            if (
              newX < 0 ||
              newX >= BOARD_WIDTH ||
              newY >= BOARD_HEIGHT ||
              (newY >= 0 && board[newY][newX])
            ) {
              return true;
            }
          }
        }
      }
      return false;
    },
    [board]
  );

  // Rotate piece
  const rotatePiece = useCallback((piece) => {
    const newShape = piece.shape[0].map((_, i) =>
      piece.shape.map((row) => row[i]).reverse()
    );
    return { ...piece, shape: newShape };
  }, []);

  // Get ghost position
  const getGhostPosition = useCallback(
    (piece) => {
      if (!piece) return null;

      let ghostY = piece.position.y;
      while (!checkCollision(piece, { ...piece.position, y: ghostY + 1 })) {
        ghostY++;
      }

      return {
        ...piece,
        position: { ...piece.position, y: ghostY },
      };
    },
    [checkCollision]
  );

  // Move piece
  const movePiece = useCallback(
    (direction) => {
      if (!currentPiece || gameOver || isPaused) return;

      const newPosition = {
        x:
          currentPiece.position.x +
          (direction === "left" ? -1 : direction === "right" ? 1 : 0),
        y: currentPiece.position.y + (direction === "down" ? 1 : 0),
      };

      if (!checkCollision(currentPiece, newPosition)) {
        const newPiece = { ...currentPiece, position: newPosition };
        setCurrentPiece(newPiece);
        setGhostPiece(getGhostPosition(newPiece));
      } else if (direction === "down") {
        // Lock piece in place
        const newBoard = [...board];
        currentPiece.shape.forEach((row, y) => {
          row.forEach((value, x) => {
            if (value) {
              const boardY = currentPiece.position.y + y;
              const boardX = currentPiece.position.x + x;
              if (boardY >= 0) {
                newBoard[boardY][boardX] = currentPiece.color;
              }
            }
          });
        });
        setBoard(newBoard);

        // Immediately check and clear lines
        const linesCleared = clearLinesImmediate(newBoard);

        // Only spawn new piece if no lines were cleared
        if (linesCleared === 0) {
          const newPiece = getRandomTetromino();
          setCurrentPiece(newPiece);
          setGhostPiece(getGhostPosition(newPiece));

          // Check for game over
          if (
            checkCollision(newPiece, {
              x: Math.floor(BOARD_WIDTH / 2) - 1,
              y: 0,
            })
          ) {
            setGameOver(true);
            handleGameOver();
          }
        }
      }
    },
    [
      currentPiece,
      board,
      gameOver,
      isPaused,
      checkCollision,
      getRandomTetromino,
      getGhostPosition,
    ]
  );

  // Handle rotation
  const handleRotate = useCallback(() => {
    if (!currentPiece || gameOver || isPaused) return;

    const rotatedPiece = rotatePiece(currentPiece);
    if (!checkCollision(rotatedPiece, currentPiece.position)) {
      setCurrentPiece(rotatedPiece);
      setGhostPiece(getGhostPosition(rotatedPiece));
    }
  }, [
    currentPiece,
    gameOver,
    isPaused,
    rotatePiece,
    checkCollision,
    getGhostPosition,
  ]);

  // Immediate line clearing function
  const clearLinesImmediate = useCallback(
    (currentBoard) => {
      let linesCleared = 0;
      const linesToClear = [];

      // Find lines to clear
      currentBoard.forEach((row, index) => {
        if (row.every((cell) => cell !== 0)) {
          linesToClear.push(index);
          linesCleared++;
        }
      });

      if (linesCleared > 0) {
        // Create new board without cleared lines
        const newBoard = currentBoard.filter(
          (_, index) => !linesToClear.includes(index)
        );

        // Add new empty lines at the top
        const updatedBoard = [
          ...Array(linesCleared)
            .fill()
            .map(() => Array(BOARD_WIDTH).fill(0)),
          ...newBoard,
        ];

        // Update score and level
        const newLines = lines + linesCleared;
        const newScore = score + linesCleared * POINTS_PER_LINE * level;
        const newLevel = Math.floor(newLines / 10) + 1;

        // Save to localStorage
        localStorage.setItem("tetrisLines", newLines.toString());
        localStorage.setItem("tetrisScore", newScore.toString());
        localStorage.setItem("tetrisLevel", newLevel.toString());

        // Create points animation
        const points = linesCleared * POINTS_PER_LINE * level;
        const pointsElement = document.createElement("div");
        pointsElement.className = styles.pointsAnimation;
        pointsElement.textContent = `+${points}`;
        document.querySelector(`.${styles.board}`).appendChild(pointsElement);

        // Update state immediately
        setLines(newLines);
        setScore(newScore);
        setLevel(newLevel);
        setBoard(updatedBoard);

        // Remove points element after animation
        setTimeout(() => {
          pointsElement.remove();
        }, 500);

        // Check for high score
        if (newScore > highScore) {
          setHighScore(newScore);
          localStorage.setItem("tetrisHighScore", newScore.toString());
          setIsNewHighScore(true);
          setTimeout(() => setIsNewHighScore(false), 1000);
        }

        // Spawn new piece after line clear
        const newPiece = getRandomTetromino();
        setCurrentPiece(newPiece);
        setGhostPiece(getGhostPosition(newPiece));

        // Check for game over
        if (
          checkCollision(newPiece, {
            x: Math.floor(BOARD_WIDTH / 2) - 1,
            y: 0,
          })
        ) {
          setGameOver(true);
          handleGameOver();
        }
      }

      return linesCleared;
    },
    [
      lines,
      score,
      level,
      highScore,
      getRandomTetromino,
      getGhostPosition,
      checkCollision,
    ]
  );

  // Game loop - only handle piece movement
  useEffect(() => {
    if (gameOver || isPaused || showInstructions) return;

    const gameLoop = setInterval(() => {
      movePiece("down");
    }, Math.max(100, INITIAL_SPEED - (level - 1) * SPEED_INCREMENT));

    return () => clearInterval(gameLoop);
  }, [gameOver, isPaused, showInstructions, movePiece, level]);

  // Handle keyboard controls
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (gameOver || isPaused || showInstructions) return;

      switch (e.key) {
        case "ArrowLeft":
          movePiece("left");
          break;
        case "ArrowRight":
          movePiece("right");
          break;
        case "ArrowDown":
          movePiece("down");
          break;
        case "ArrowUp":
          handleRotate();
          break;
        case " ":
          // Hard drop
          while (
            !checkCollision(currentPiece, {
              ...currentPiece.position,
              y: currentPiece.position.y + 1,
            })
          ) {
            movePiece("down");
          }
          break;
        case "p":
          setIsPaused(!isPaused);
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [
    gameOver,
    isPaused,
    showInstructions,
    movePiece,
    handleRotate,
    currentPiece,
    checkCollision,
  ]);

  // Initialize game
  useEffect(() => {
    const newPiece = getRandomTetromino();
    setCurrentPiece(newPiece);
    setGhostPiece(getGhostPosition(newPiece));
    logPageView("tetris", "tetris");
  }, [getRandomTetromino, getGhostPosition]);

  // Handle game over
  const handleGameOver = async () => {
    if (score > 0) {
      try {
        const apiData = {
          gamePoints: score,
          gameType: "tetris",
        };
        const res = await userGameRewards(apiData);
        if (res) {
          updateUserInfo((prev) => ({
            ...prev,
            isLoading: false,
          }));
          updatewatchScreenInfo((prev) => ({
            ...prev,
            LoginMessage: res?.message,
          }));
        }
      } catch (error) {
        console.error("API call failed:", error);
      }
    }
  };

  // Reset game
  const resetGame = () => {
    setBoard(createEmptyBoard());
    const newPiece = getRandomTetromino();
    setCurrentPiece(newPiece);
    setGhostPiece(getGhostPosition(newPiece));
    setGameOver(false);
    setScore(0);
    setLevel(1);
    setLines(0);
    setIsPaused(false);

    // Clear localStorage
    localStorage.removeItem("tetrisScore");
    localStorage.removeItem("tetrisLevel");
    localStorage.removeItem("tetrisLines");
  };

  return (
    <div className={styles.container}>
      <h3 className="welcometextmeme">THE NEXT TETRIS</h3>

      <div className={styles.gameContainer}>
        <div className={styles.infoPanel}>
          <div className={styles.infoRow}>
            <div className={styles.infoCol}>
              <div className={styles.infoLabel}>Score</div>
              <div className={styles.infoValue}>{score}</div>
            </div>
            <div className={styles.infoCol}>
              <div className={styles.infoLabel}>Lines</div>
              <div className={styles.infoValue}>{lines}</div>
            </div>
          </div>
          <div className={styles.levelRow}>
            <div className={styles.levelLabel}>Level {level}</div>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{
                  width: `${((lines % 10) / 10) * 100}%`,
                  backgroundColor: `hsl(${(level * 30) % 360}, 70%, 60%)`,
                }}
              />
            </div>
          </div>
        </div>

        <div className={styles.boardContainer}>
          <div className={styles.board}>
            {board.map((row, y) => (
              <div key={y} className={styles.row}>
                {row.map((cell, x) => (
                  <div
                    key={`${y}-${x}`}
                    className={styles.cell}
                    style={{
                      backgroundColor: cell || "transparent",
                      width: "30px",
                      height: "30px",
                      boxSizing: "border-box",
                    }}
                  />
                ))}
              </div>
            ))}
            {ghostPiece &&
              !gameOver &&
              ghostPiece.shape.map((row, y) =>
                row.map(
                  (cell, x) =>
                    cell && (
                      <div
                        key={`ghost-${y}-${x}`}
                        className={`${styles.cell} ${styles.ghostCell}`}
                        style={{
                          position: "absolute",
                          left: `${(ghostPiece.position.x + x) * 24}px`,
                          top: `${(ghostPiece.position.y + y) * 24}px`,
                          width: "24px",
                          height: "24px",
                          boxSizing: "border-box",
                        }}
                      />
                    )
                )
              )}
            {currentPiece &&
              !gameOver &&
              currentPiece.shape.map((row, y) =>
                row.map(
                  (cell, x) =>
                    cell && (
                      <div
                        key={`piece-${y}-${x}`}
                        className={`${styles.cell} ${styles.droppingPiece} ${
                          isPaused ? styles.pausedPiece : ""
                        }`}
                        style={{
                          position: "absolute",
                          backgroundColor: currentPiece.color,
                          left: `${(currentPiece.position.x + x) * 24}px`,
                          top: `${(currentPiece.position.y + y) * 24}px`,
                          width: "24px",
                          height: "24px",
                          boxSizing: "border-box",
                          border: "1px solid rgba(255, 255, 255, 0.2)",
                        }}
                      />
                    )
                )
              )}
          </div>
          <div className={styles.boardControls}>
            <button
              className={styles.boardControlBtn}
              onClick={resetGame}
              title="Reset Game"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
              </svg>
            </button>
            <button
              className={styles.boardControlBtn}
              onClick={() => setIsPaused(!isPaused)}
              disabled={gameOver}
              title={isPaused ? "Resume Game" : "Pause Game"}
            >
              {isPaused ? (
                <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
                  <path d="M8 5v14l11-7z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className={styles.controls}>
          <button
            className={styles.controlBtn}
            onClick={() => movePiece("left")}
            disabled={gameOver || isPaused}
          >
            <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
          </button>
          <button
            className={styles.controlBtn}
            onClick={() => movePiece("right")}
            disabled={gameOver || isPaused}
          >
            <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
            </svg>
          </button>
          <button
            className={styles.controlBtn}
            onClick={() => movePiece("down")}
            disabled={gameOver || isPaused}
          >
            <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
              <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
            </svg>
          </button>
          <button
            className={styles.controlBtn}
            onClick={handleRotate}
            disabled={gameOver || isPaused}
          >
            <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
              <path d="M7.11 8.53L5.7 7.11C4.8 8.27 4.24 9.61 4.07 11h2.02c.14-.87.49-1.72 1.02-2.47zM6.09 13H4.07c.17 1.39.72 2.73 1.62 3.89l1.41-1.42c-.52-.75-.87-1.59-1.01-2.47zm1.01 5.32c1.16.9 2.51 1.44 3.9 1.61V17.9c-.87-.15-1.71-.49-2.46-1.03L7.1 18.32zM13 4.07V1L8.45 5.55 13 10V6.09c2.84.48 5 2.94 5 5.91s-2.16 5.43-5 5.91v2.02c3.95-.49 7-3.85 7-7.93s-3.05-7.44-7-7.93z" />
            </svg>
          </button>
        </div>
      </div>
      <p
        className={styles.quest}
        onClick={() => setShowInstructions(true)}
        title="How to Play"
      >
        <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z" />
        </svg>
      </p>

      <img
        onClick={toogleMenu}
        src="https://d1ccopf5cw1hft.cloudfront.net/s3data/cancelicon.svg"
        className={styles.cancel_full}
        style={{ cursor: "pointer" }}
      />

      {gameOver && (
        <div className={styles.overlay}>
          <div className={styles.quento_full_popup}>
            <div className={styles.quento_popup_content}>
              <h2 className="welcometextmeme">Game Over!</h2>
              <img
                src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                className={styles.quento_cancel_img}
                onClick={resetGame}
              />
              <div className={styles.quento_epic_div}>
                <p className={styles.quento_pointsLabel}>EARNED TOKENS</p>
                <div className={styles.quento_pointsValue1}>
                  <img
                    src="https://d1ccopf5cw1hft.cloudfront.net/s3data/coinlogo.png"
                    alt="coin"
                  />
                  <p className={styles.quento_pointsNumber}>{score}</p>
                </div>
              </div>
              <p className={styles.quento_bottom_text}>
                Keep playing! Challenge yourself to beat your high score!
              </p>
              <button className={styles.control_btn} onClick={resetGame}>
                Play Again
              </button>
            </div>
          </div>
        </div>
      )}

      {showInstructions && (
        <div className={styles.overlay}>
          <div className={styles.quento_full_popup}>
            <div className={styles.quento_popup_content}>
              <h2 className="welcometextmeme">How to Play</h2>
              <img
                src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                className={styles.quento_cancel_img}
                onClick={() => setShowInstructions(false)}
              />
              <div className={styles.quento_epic_div}>
                <div className={styles.instruction_item}>
                  <div className={styles.instruction_icon}>←→</div>
                  <p className={styles.instruction_text}>
                    Move pieces left and right
                  </p>
                </div>
                <div className={styles.instruction_item}>
                  <div className={styles.instruction_icon}>↓</div>
                  <p className={styles.instruction_text}>
                    Move piece down faster
                  </p>
                </div>
                <div className={styles.instruction_item}>
                  <div className={styles.instruction_icon}>↻</div>
                  <p className={styles.instruction_text}>Rotate piece</p>
                </div>
                <div className={styles.instruction_item}>
                  <div className={styles.instruction_icon}>💯</div>
                  <p className={styles.instruction_text}>
                    Get $MEMETV Tokens by clearing lines
                  </p>
                </div>
                <div className={styles.instruction_item}>
                  <div className={styles.instruction_icon}>⬆️</div>
                  <p className={styles.instruction_text}>
                    Level up every 10 lines cleared
                  </p>
                </div>
                <div className={styles.instruction_item}>
                  <div className={styles.instruction_icon}>⚡</div>
                  <p className={styles.instruction_text}>
                    Higher levels = faster speed & more $MEMETV tokens
                  </p>
                </div>
              </div>
              <button
                className={styles.control_btn}
                onClick={() => setShowInstructions(false)}
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tetris;
